import React, { FC, ReactNode, useEffect, useState } from 'react';

import { bounceIn } from '@lucid/theme';
import {
  BeaconEventModalName,
  sendBeaconEvent,
  getI18nData,
  I18nDataEdges
} from '@lucid/utils';
import Modal from 'react-modal';

export interface BounceDetectionProps {
  children: ReactNode;
  forceBounce?: boolean;
  i18nKey?: string;
  i18nData: I18nDataEdges;
}

const BounceDetection: FC<BounceDetectionProps> = ({
  children,
  forceBounce = false,
  i18nKey = 'en',
  i18nData
}) => {
  const [hasBounced, setHasBounced] = useState(forceBounce || false);
  const loadDelay = 2000;

  useEffect(() => {
    /* eslint-disable-next-line @typescript-eslint/no-var-requires */
    const bounceback = require('@lucid/utils/src/utils/bounceback');
    window.setTimeout(() => {
      bounceback.enable();
      bounceback.init({
        onBounce: () => {
          setHasBounced(true);
          if (window && window.lucid && window.lucid.kissMetrics) {
            window.lucid.kissMetrics.set({ '[website] bounce saw dialog': 1 });
          }
          sendBeaconEvent({
            name: 'shownModal',
            modalName: BeaconEventModalName.bounceDetection
          });
          return bounceback;
        },
        cookieLife: 3,
        storeName: 'exit-intent-marketing',
        delay: 500
      });
    }, loadDelay);

    return () => {
      bounceback.disable();
    };
  }, []);

  const closeBounceDetection = () => {
    setHasBounced(false);
  };

  const i18n = getI18nData(i18nData, i18nKey);

  Modal.setAppElement('body');

  return (
    <Modal
      shouldCloseOnOverlayClick
      isOpen={hasBounced}
      onRequestClose={closeBounceDetection}
      style={{
        overlay: {
          zIndex: 9000,
          backgroundColor: 'rgba(0, 0, 0, 0.4) !important',
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        },
        content: {
          maxWidth: '95%',
          position: 'fixed',
          top: '50%',
          right: 'unset',
          bottom: 'unset',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }
      }}
    >
      <aside
        css={{
          background: '#FFFFFF',
          padding: '1.25rem 2.5rem 2.5rem',
          borderRadius: '0.3125rem',
          width: '32rem',
          outline: 'none',
          margin: 'auto',
          position: 'relative',
          color: '#4C535D',
          opacity: '1',
          animation: bounceIn,
          ['@media only screen and (max-width: 768px)']: {
            padding: '15px',
            maxWidth: '100%'
          },
          '& .bounce-detection--loaded': {
            opacity: 1
          },
          '& .bounce-detection__header': {
            fontSize: '1.6875rem',
            fontWeight: 700
          },
          '& .bounce-detection__body': {
            fontSize: '1.125rem'
          },
          '& .bounce-detection--survey': {
            width: '600px',
            maxWidth: '100%',
            iframe: {
              width: '100%',
              height: '500px',
              minHeight: '368px',
              maxHeight: '85vh'
            }
          },
          em: {
            textDecoration: 'underline'
          }
        }}
        data-reject={i18n('mktg-bounce-detection-rejection')}
      >
        <>
          <button
            css={{
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
              outline: 'none',
              border: 0,
              color: '#A9AFB8',
              fontSize: '1rem',
              background: 'transparent',
              '&:hover': {
                cursor: 'pointer',
                color: '#635DFF'
              }
            }}
            onClick={closeBounceDetection}
            type="button"
          >
            ×
          </button>
          {children}
        </>
      </aside>
    </Modal>
  );
};

export { BounceDetection };
export default BounceDetection;
